import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RLSPolicyEvaluator = ({ user, refreshTrigger, onEvent }) => {
  const [policies, setPolicies] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState(false);

  const rlsTests = [
    {
      id: 'select_own',
      name: 'SELECT Own Profile',
      description: 'Can read own user_profiles record',
      test: async () => {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
        return { success: !error, data, error };
      }
    },
    {
      id: 'update_own',
      name: 'UPDATE Own Profile',
      description: 'Can update own user_profiles record',
      test: async () => {
        const { data, error } = await supabase?.from('user_profiles')?.update({ updated_at: new Date()?.toISOString() })?.eq('id', user?.id)?.select()?.single();
        return { success: !error, data, error };
      }
    },
    {
      id: 'select_audit_logs',
      name: 'SELECT Own Audit Logs',
      description: 'Can read own audit_logs records',
      test: async () => {
        const { data, error } = await supabase?.from('audit_logs')?.select('*')?.eq('user_id', user?.id)?.limit(1);
        return { success: !error, data, error };
      }
    },
    {
      id: 'insert_audit_logs',
      name: 'INSERT Audit Logs',
      description: 'Can insert audit_logs records',
      test: async () => {
        const { data, error } = await supabase?.from('audit_logs')?.insert({
            user_id: user?.id,
            action_type: 'profile_viewed',
            action_description: 'RLS policy test',
            success: true
          })?.select()?.single();
        return { success: !error, data, error };
      }
    }
  ];

  useEffect(() => {
    loadPolicyInfo();
  }, [refreshTrigger]);

  const loadPolicyInfo = () => {
    // Static policy information based on migrations
    const policyInfo = [
      {
        table: 'user_profiles',
        policy: 'users_manage_own_user_profiles',
        operations: ['SELECT', 'INSERT', 'UPDATE'],
        condition: 'id = auth.uid()',
        status: 'active'
      },
      {
        table: 'audit_logs',
        policy: 'users_view_own_audit_logs',
        operations: ['SELECT'],
        condition: 'user_id = auth.uid()',
        status: 'active'
      },
      {
        table: 'audit_logs',
        policy: 'system_insert_audit_logs',
        operations: ['INSERT'],
        condition: 'user_id = auth.uid()',
        status: 'active'
      }
    ];

    setPolicies(policyInfo);
    onEvent?.('RLS policy information loaded', 'info', policyInfo);
  };

  const runAllTests = async () => {
    setTesting(true);
    onEvent?.('Running RLS policy tests', 'action');
    const results = {};

    for (const test of rlsTests) {
      try {
        const result = await test?.test();
        results[test.id] = {
          ...result,
          timestamp: new Date()
        };
        onEvent?.(
          `RLS test ${test?.id}: ${result?.success ? 'PASSED' : 'FAILED'}`,
          result?.success ? 'success' : 'error',
          result
        );
      } catch (error) {
        results[test.id] = {
          success: false,
          error: { message: error?.message },
          timestamp: new Date()
        };
        onEvent?.(`RLS test ${test?.id}: EXCEPTION`, 'error', error);
      }
    }

    setTestResults(results);
    setTesting(false);
  };

  const runSingleTest = async (test) => {
    setTesting(true);
    onEvent?.(`Running RLS test: ${test?.name}`, 'action');

    try {
      const result = await test?.test();
      setTestResults(prev => ({
        ...prev,
        [test?.id]: {
          ...result,
          timestamp: new Date()
        }
      }));
      onEvent?.(
        `RLS test ${test?.id}: ${result?.success ? 'PASSED' : 'FAILED'}`,
        result?.success ? 'success' : 'error',
        result
      );
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [test?.id]: {
          success: false,
          error: { message: error?.message },
          timestamp: new Date()
        }
      }));
      onEvent?.(`RLS test ${test?.id}: EXCEPTION`, 'error', error);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">RLS Policy Evaluation</h2>
          <p className="text-sm text-gray-400 mt-1">
            Row Level Security policy status and access control testing
          </p>
        </div>
        <Button
          variant="outline"
          onClick={runAllTests}
          disabled={testing}
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <AppIcon name="Play" className="w-4 h-4 mr-2" />
          Run All Tests
        </Button>
      </div>
      {/* Active Policies */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">Active RLS Policies</h3>
        <div className="space-y-2">
          {policies?.map((policy, index) => (
            <div
              key={index}
              className="bg-gray-800 rounded-lg border border-gray-700 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <AppIcon name="Shield" className="w-4 h-4 text-green-500" />
                    <span className="font-medium text-white">{policy?.policy}</span>
                    <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded">
                      {policy?.status}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-gray-400">
                    Table: <span className="text-cyan-400 font-mono">{policy?.table}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-400">
                    Operations: {policy?.operations?.map((op) => (
                      <span key={op} className="text-blue-400 font-mono ml-2">{op}</span>
                    ))}
                  </div>
                  <div className="mt-1 text-sm text-gray-400">
                    Condition: <span className="text-yellow-300 font-mono">{policy?.condition}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Policy Tests */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">Policy Tests</h3>
        <div className="space-y-2">
          {rlsTests?.map((test) => {
            const result = testResults?.[test?.id];
            return (
              <div
                key={test?.id}
                className="bg-gray-800 rounded-lg border border-gray-700 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {result ? (
                        result?.success ? (
                          <AppIcon name="CheckCircle" className="w-5 h-5 text-green-500" />
                        ) : (
                          <AppIcon name="XCircle" className="w-5 h-5 text-red-500" />
                        )
                      ) : (
                        <AppIcon name="Circle" className="w-5 h-5 text-gray-500" />
                      )}
                      <span className="font-medium text-white">{test?.name}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{test?.description}</p>

                    {result && (
                      <div className={`mt-3 text-sm rounded px-3 py-2 ${
                        result?.success
                          ? 'bg-green-900/20 text-green-400' :'bg-red-900/20 text-red-400'
                      }`}>
                        <div className="font-medium">
                          {result?.success ? '✓ Test Passed' : '✗ Test Failed'}
                        </div>
                        {result?.error && (
                          <div className="text-xs mt-1 font-mono">
                            {result?.error?.message}
                          </div>
                        )}
                        <div className="text-xs mt-1 text-gray-400">
                          {result?.timestamp?.toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runSingleTest(test)}
                    disabled={testing}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 flex-shrink-0"
                  >
                    <AppIcon name="Play" className="w-3 h-3 mr-1" />
                    Test
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RLSPolicyEvaluator;